/*
 * form-validation.js
 * -------------------------------------------------------------------------
 * Replaces the browser's NATIVE constraint-validation bubble (whose language
 * follows the user's browser, not the storefront) with an inline message
 * rendered below the field, using locale strings from window.validationStrings
 * (populated via Liquid `| t` in theme.liquid, so it follows the STORE locale).
 *
 * It does NOT change which fields are valid/required and never blocks or
 * allows submission differently: preventing the `invalid` event's default only
 * suppresses the native bubble UI — an invalid form still will not submit.
 *
 * Opt-out: add `data-skip-custom-validation` to a field or any ancestor
 * (e.g. a form) that already renders its own validation messages.
 * -------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ERROR_CLASS = 'js-field-error';
  var strings = window.validationStrings || {};
  var uid = 0;
  var focusPending = false;

  function shouldSkip(input) {
    if (!input || !input.willValidate) return true;
    if (input.disabled || input.type === 'hidden') return true;
    if (input.closest && input.closest('[data-skip-custom-validation]')) return true;
    return false;
  }

  // Map the failing validity state to a locale string.
  // Falls back to the field's own `title`, then to the native message, so a
  // field never ends up with an empty error.
  function messageFor(input) {
    var v = input.validity;
    if (v.valueMissing) return strings.required || input.validationMessage;
    if (v.typeMismatch) {
      if (input.type === 'email') return strings.email || input.validationMessage;
      if (input.type === 'url') return strings.url || input.validationMessage;
      return strings.pattern || input.validationMessage;
    }
    if (v.patternMismatch) return input.title || strings.pattern || input.validationMessage;
    // tooShort/tooLong/range*/step/badInput: prefer an author-provided title.
    return input.title || strings.pattern || input.validationMessage;
  }

  // The error element is inserted right after the field's wrapper so it sits
  // directly below the control. CSS decides whether it flows in-place or is
  // absolutely positioned (to avoid disturbing e.g. an inline input+button row).
  function anchorFor(input) {
    // For checkbox/radio, place the message after the whole row (control + its
    // label text) rather than inside the label next to the box.
    if (input.type === 'checkbox' || input.type === 'radio') {
      return input.closest('.check, .checkbox, .form__label, li, label') || input.parentNode || input;
    }
    return (input.closest && input.closest('.wpcf7-form-control-wrap')) || input;
  }

  function showError(input) {
    if (shouldSkip(input)) return;

    var el = input._jsFieldError;
    if (!el || !el.isConnected) {
      el = document.createElement('span');
      el.className = ERROR_CLASS;
      el.setAttribute('role', 'alert');
      el.id = ERROR_CLASS + '-' + (++uid);
      var anchor = anchorFor(input);
      anchor.parentNode.insertBefore(el, anchor.nextSibling);
      input._jsFieldError = el;
      input.setAttribute('aria-describedby',
        (input.getAttribute('aria-describedby') ? input.getAttribute('aria-describedby') + ' ' : '') + el.id);
    }
    el.textContent = messageFor(input);
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('is-invalid');
    if (input.form) input.form.classList.add('has-validation-error');

    // Focus the first invalid field of this validation round (mirrors native).
    if (!focusPending) {
      focusPending = true;
      try { input.focus(); } catch (e) {}
      setTimeout(function () { focusPending = false; }, 0);
    }
  }

  function clearError(input) {
    var el = input._jsFieldError;
    if (el) {
      var describedby = (input.getAttribute('aria-describedby') || '')
        .split(' ').filter(function (id) { return id && id !== el.id; }).join(' ');
      if (describedby) input.setAttribute('aria-describedby', describedby);
      else input.removeAttribute('aria-describedby');
      if (el.parentNode) el.parentNode.removeChild(el);
      input._jsFieldError = null;
    }
    input.removeAttribute('aria-invalid');
    input.classList.remove('is-invalid');
    if (input.form && !input.form.querySelector('.' + ERROR_CLASS)) {
      input.form.classList.remove('has-validation-error');
    }
  }

  // `invalid` does not bubble → listen in the capture phase.
  document.addEventListener('invalid', function (e) {
    var input = e.target;
    if (shouldSkip(input)) return;
    e.preventDefault(); // suppress the native bubble; keep native validity intact
    showError(input);
  }, true);

  // Re-validate as the user edits. Use validity.valid (a property read) rather
  // than checkValidity() so we don't dispatch another `invalid` event.
  function onEdit(e) {
    var input = e.target;
    if (!input || !input.willValidate || input.type === 'hidden') return;
    if (input.validity.valid) {
      clearError(input);
    } else if (input._jsFieldError) {
      input._jsFieldError.textContent = messageFor(input);
    }
  }
  document.addEventListener('input', onEdit, true);
  document.addEventListener('change', onEdit, true);
})();
